import { Typography } from '@euler/app/components/typography';
import { TypewriterText } from '@euler/components/typography/TypewriterText';
import { useTypewriter } from '@euler/components/typography/useTypewriter';
import { earliestTrialEnd } from '@euler/functions/entitlements/earliestTrialEnd';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  AiHomeActivity,
  AiHomeActivityResult,
  AiHomeApdmCounts,
  AiHomeFeature,
  AiHomeSuggestion,
} from '@euler/model/ai-home';
import { AiAttachmentInfo } from '@euler/model/apdm';
import { LinearGradient } from 'expo-linear-gradient';
import { StatusBar } from 'expo-status-bar';
import { ChevronLeft, History, Menu, Sparkles } from 'lucide-react-native';
import { memo, useMemo, useState } from 'react';
import { LayoutChangeEvent, Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  interpolate,
  runOnJS,
  useAnimatedReaction,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AiSuiteHomeTool, aiColors, kAiHeroGradient } from '../theme';
import { AiPromptCard } from './AiPromptCard';
import { AiQuickActions } from './AiQuickActions';
import { AiRecentActivity } from './AiRecentActivity';
import { AiSectionHeader } from './AiSectionHeader';
import { AiToolBadge, AiToolTile } from './AiToolTile';
import { AiTrialPill } from './AiTrialPill';
import { AiUpgradeCard, AiUpgradeFrom } from './AiUpgradeCard';
import { AiWorkshopToolsSection } from './AiWorkshopToolsSection';
import { AiTrialCredits } from './useAiTrialCredits';
import { AiWorkshopToolKind, AiWorkshopTools } from './useAiWorkshopTools';
import { useEntitledAiHomeTools } from './useEntitledAiHomeTools';

const kNavRowHeight = 52;
const kIntro = '有什么想做的，直接告诉我；也可以上传客户表格开始。';

export interface AiHomeSuggestionsState {
  items: AiHomeSuggestion[] | null;
  failed: boolean;
}

export const AiHomeView = memo(
  ({
    name,
    focused,
    leading,
    onLeading,
    onHistory,
    onSubmit,
    onTool,
    onSuggestion,
    onRetrySuggestions,
    suggestions,
    activity,
    wide,
    onActivity,
    onMoreActivity,
    workshop,
    onWorkshopTool,
    upgrade,
    aiTrial,
    onUpgrade,
  }: {
    name: string;
    /** It sets the status bar only while on screen: a tab stays mounted, and
     * a mounted status bar would keep its style over the other tabs. */
    focused: boolean;
    /** `menu` opens the drawer on a phone; `back` leaves a pushed screen. */
    leading: 'menu' | 'back' | null;
    onLeading: () => void;
    /** Null where the history has its own column. */
    onHistory: (() => void) | null;
    onSubmit: (text: string, attachments: AiAttachmentInfo[]) => boolean;
    onTool: (tool: AiSuiteHomeTool) => void;
    onSuggestion: (item: AiHomeSuggestion) => void;
    onRetrySuggestions: () => void;
    suggestions: AiHomeSuggestionsState;
    /** Null until it loads, or when it could not. */
    activity: AiHomeActivityResult | null;
    wide: boolean;
    onActivity: (item: AiHomeActivity) => void;
    onMoreActivity: () => void;
    /** An AI-edition store's workshop tools; null for any other store. */
    workshop: AiWorkshopTools | null;
    onWorkshopTool: (kind: AiWorkshopToolKind) => void;
    /** The full-edition invitation, for a store short of it; else null. */
    upgrade: { from: AiUpgradeFrom; busy: boolean } | null;
    /** A workshop store trying AI, under the title; else null. */
    aiTrial: AiTrialCredits | null;
    onUpgrade: () => void;
  }) => {
    const tools = useEntitledAiHomeTools();
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const styles = useStyles();
    const scrollY = useSharedValue(0);
    const [collapsed, setCollapsed] = useState(false);
    const navHeight = insets.top + kNavRowHeight;
    // The bar turns solid once the greeting has scrolled under it, or at the
    // end of the scroll on a wide screen where the page barely scrolls, so
    // it never stops half faded.
    const [extent, setExtent] = useState({ content: 0, viewport: 0 });
    const collapseAt = Math.max(
      1,
      Math.min(64, extent.content - extent.viewport),
    );
    const onScroll = useAnimatedScrollHandler(event => {
      scrollY.value = event.contentOffset.y;
    });
    useAnimatedReaction(
      () => scrollY.value > collapseAt * 0.6,
      (next, previous) => {
        if (next !== previous) runOnJS(setCollapsed)(next);
      },
      [collapseAt],
    );
    const shade = useAnimatedStyle(() => ({
      opacity: interpolate(scrollY.value, [0, collapseAt], [0, 1], 'clamp'),
    }));
    const onBlue = useAnimatedStyle(() => ({
      opacity: interpolate(scrollY.value, [0, collapseAt], [1, 0], 'clamp'),
    }));
    const hello = `${greeting()}，${name}！`;
    const [helloTyped, introTyped] = useTypewriter([hello, kIntro]);
    const LeadingIcon = leading === 'menu' ? Menu : ChevronLeft;
    const navButton = (tone: 'light' | 'dark', icon: 'leading' | 'history') => {
      const color =
        tone === 'light' ? aiColors.primaryForeground : aiColors.foreground;
      const Icon = icon === 'leading' ? LeadingIcon : History;
      return (
        <View
          style={[styles.navButton, tone === 'light' && styles.navButtonLight]}
        >
          <Icon size={20} color={color} />
        </View>
      );
    };
    const title = (tone: 'light' | 'dark') => (
      <View style={styles.titleBlock} pointerEvents="none">
        <View style={styles.titleRow}>
          <Sparkles
            size={16}
            color={
              tone === 'light' ? aiColors.primaryForeground : aiColors.primary
            }
          />
          <Typography.Text
            medium
            size={rpx(16)}
            color={
              tone === 'light'
                ? aiColors.primaryForeground
                : aiColors.foreground
            }
          >
            知车 AI
          </Typography.Text>
        </View>
        {aiTrial ? <AiTrialPill trial={aiTrial} tone={tone} /> : null}
      </View>
    );
    return (
      <View style={styles.page}>
        {focused ? <StatusBar style={collapsed ? 'dark' : 'light'} /> : null}
        <Animated.ScrollView
          onScroll={onScroll}
          onLayout={(event: LayoutChangeEvent) => {
            const viewport = event.nativeEvent.layout.height;
            setExtent(previous => ({ ...previous, viewport }));
          }}
          onContentSizeChange={(_: number, content: number) =>
            setExtent(previous => ({ ...previous, content }))
          }
          scrollEventThrottle={16}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          contentContainerStyle={{ paddingBottom: insets.bottom + rpx(32) }}
        >
          <View style={[styles.hero, { paddingTop: navHeight + rpx(8) }]}>
            {/* Pulling down past the top still shows blue, not a gap. */}
            <View style={styles.heroOverscroll} />
            <LinearGradient
              colors={kAiHeroGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
            <View style={styles.bubbleLarge} />
            <View style={styles.bubbleSmall} />
            <View style={styles.content}>
              <TypewriterText
                text={hello}
                typed={helloTyped}
                bold
                size={rpx(22)}
                color={aiColors.primaryForeground}
              />
              <TypewriterText
                text={kIntro}
                typed={introTyped}
                size={rpx(14)}
                lineHeight={rpx(21)}
                color="rgba(255,255,255,0.78)"
                style={{ marginTop: rpx(4) }}
              />
            </View>
          </View>
          <View style={[styles.content, styles.body]}>
            <AiPromptCard onSubmit={onSubmit} />
            {activity ? (
              <AiRecentActivity
                items={activity.items}
                more={activity.total > activity.items.length}
                wide={wide}
                onOpen={onActivity}
                onMore={onMoreActivity}
              />
            ) : null}
            <AiSectionHeader
              title="您的超级 AI 智能工作搭子"
              trialEndsAt={earliestTrialEnd(tools.map(x => x.trialEndsAt))}
            />
            <View style={styles.toolGrid}>
              {tools.map(({ tool }) => (
                <AiToolTile
                  key={tool.feature}
                  tool={tool}
                  badge={toolBadge(tool, activity?.apdm ?? null)}
                  onPress={() => onTool(tool)}
                />
              ))}
            </View>
            {workshop != null ? (
              <AiWorkshopToolsSection
                workshop={workshop}
                onPress={onWorkshopTool}
              />
            ) : null}
            <AiQuickActions
              state={suggestions}
              onPress={onSuggestion}
              onRetry={onRetrySuggestions}
            />
            {upgrade != null ? (
              <AiUpgradeCard
                from={upgrade.from}
                busy={upgrade.busy}
                onPress={onUpgrade}
              />
            ) : null}
          </View>
        </Animated.ScrollView>
        <View
          style={[styles.nav, { height: navHeight, paddingTop: insets.top }]}
        >
          <Animated.View
            style={[StyleSheet.absoluteFill, styles.navShade, shade]}
          />
          <View style={styles.navRow}>
            {leading ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={leading === 'menu' ? '打开菜单' : '返回'}
                onPress={onLeading}
              >
                <Animated.View style={onBlue}>
                  {navButton('light', 'leading')}
                </Animated.View>
                <Animated.View style={[StyleSheet.absoluteFill, shade]}>
                  {navButton('dark', 'leading')}
                </Animated.View>
              </Pressable>
            ) : (
              <View style={styles.navButton} />
            )}
            <View style={styles.titleSlot}>
              <Animated.View style={onBlue}>{title('light')}</Animated.View>
              <Animated.View style={[StyleSheet.absoluteFill, shade]}>
                {title('dark')}
              </Animated.View>
            </View>
            {onHistory ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="历史对话"
                onPress={onHistory}
              >
                <Animated.View style={onBlue}>
                  {navButton('light', 'history')}
                </Animated.View>
                <Animated.View style={[StyleSheet.absoluteFill, shade]}>
                  {navButton('dark', 'history')}
                </Animated.View>
              </Pressable>
            ) : (
              <View style={styles.navButton} />
            )}
          </View>
        </View>
      </View>
    );
  },
);

/** A tool card's count: what is happening there, or what needs the user;
 * the section's header carries the trial. */
function toolBadge(
  tool: AiSuiteHomeTool,
  apdm: AiHomeApdmCounts | null,
): AiToolBadge | null {
  if (tool.feature !== AiHomeFeature.Apdm || !apdm) return null;
  if (apdm.newLeads > 0)
    return { text: `新线索 ${apdm.newLeads}`, attention: true };
  if (apdm.running > 0)
    return { text: `进行中 ${apdm.running}`, attention: false };
  return null;
}

function greeting(): string {
  const hour = new Date().getHours();
  return hour < 11
    ? '早上好'
    : hour < 13
    ? '中午好'
    : hour < 18
    ? '下午好'
    : '晚上好';
}

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        page: { flex: 1, backgroundColor: aiColors.background },
        content: {
          width: '100%',
          maxWidth: rpx(720),
          alignSelf: 'center',
          paddingHorizontal: rpx(16),
        },
        hero: {
          paddingBottom: rpx(56),
          overflow: 'hidden',
        },
        heroOverscroll: {
          position: 'absolute',
          left: 0,
          right: 0,
          top: -1000,
          height: 1000,
          // the gradient's top
          backgroundColor: kAiHeroGradient[0],
        },
        bubbleLarge: {
          position: 'absolute',
          right: -rpx(64),
          top: -rpx(64),
          width: rpx(192),
          height: rpx(192),
          borderRadius: rpx(96),
          backgroundColor: 'rgba(255,255,255,0.1)',
        },
        bubbleSmall: {
          position: 'absolute',
          left: -rpx(40),
          bottom: 0,
          width: rpx(128),
          height: rpx(128),
          borderRadius: rpx(64),
          backgroundColor: 'rgba(255,255,255,0.05)',
        },
        body: { marginTop: -rpx(36) },
        toolGrid: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: rpx(10),
        },
        nav: { position: 'absolute', left: 0, right: 0, top: 0 },
        navShade: {
          backgroundColor: aiColors.card,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: aiColors.border,
        },
        navRow: {
          height: kNavRowHeight,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: rpx(12),
        },
        navButton: {
          width: 40,
          height: 40,
          borderRadius: 10,
          alignItems: 'center',
          justifyContent: 'center',
        },
        navButtonLight: { backgroundColor: 'rgba(255,255,255,0.15)' },
        titleSlot: { flex: 1, alignItems: 'center' },
        titleBlock: {
          height: kNavRowHeight,
          alignItems: 'center',
          justifyContent: 'center',
        },
        titleRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        },
      }),
    [rpx],
  );
}
